import FreshChat from 'app/commons/FreshChat'
import {
  CLEAR_LOCAL_STORAGE,
  GOOGLE_MAP_STYLE,
  JAKARTA_AREA,
  LOCAL_STORAGE,
  MAP,
  SERVICE_P2D,
  WEBAPP_ASSET,
  countries,
  defaultCountries,
} from 'app/commons/constants'
import { GOOGLE_RECAPTCHA_KEY } from 'app/commons/constants/env.const'
import { getParamFromURL, isEmpty } from 'app/commons/helpers/compare'
import { crossStorageUtils } from 'app/commons/helpers/crossStorage'
import { hideLoading } from 'app/commons/helpers/loadingUtils'
import { setCookie } from 'app/commons/helpers/storage/cookie.helper'
import * as Utils from 'app/commons/helpers/utils'
import useBeforeUnload from 'app/hooks/useBeforeUnload'
import { useAppDispatch, useAppSelector } from 'app/store'
import * as authAction from 'app/store/auth/authAction'
import * as configActions from 'app/store/config/configAction'
import { mapAction } from 'app/store/map/mapSlice'
import { moengageUserAttribute } from 'app/utils/appUtils'

import GoogleMapReact from 'google-map-react'
import React, { Suspense, useEffect, useState } from 'react'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { useHistory, useLocation } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import SmsVerify from 'app/components/SmsVerify'

import Loading from './Common/Loading'
import Header from './Header/index'
import Port from './InfoWindow/Port'
import InfoWindow from './InfoWindow/index'
import Navigation from './Navigation'
import './styles.scss'

const AppMaritime = () => {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const location = useLocation()
  const [ready, setReady] = useState(false)
  const [openSmmVerify, setOpenSmmVerify] = useState(false)
  const configFTL = useAppSelector((state) => state.configuration.configFTL)
  const data = useAppSelector((state) => state.configuration.data)
  const accounts = useAppSelector((state) => state.auth.login.accounts)
  const ports = useAppSelector((state) => state.ports)
  const expandPorts = useAppSelector((state) => state.locations.expandPorts)
  const { serviceType } = useAppSelector((state) => state.locations)
  const { isLogged } = useAppSelector((state) => state.auth)

  // Fire when reloading
  useBeforeUnload()

  useEffect(() => {
    const handleListenMessage = (event) => {
      const token = getParamFromURL('token') || localStorage.getItem(LOCAL_STORAGE.ACCESS_TOKEN)
      if (event.origin === WEBAPP_ASSET) {
        let accessToken = typeof event.data === 'string' && event.data?.split(':')
        if (accessToken?.length && accessToken[0] === 'auth') {
          accessToken = accessToken[1] || ''
          if (document.hidden) window.location.reload()
          if (token === accessToken) return
          if (accessToken) {
            crossStorageUtils.setAccessToken(accessToken)
          } else {
            Utils.deleteAllStorage()
          }
          window.location.reload()
        }
      }
    }
    window.addEventListener('message', handleListenMessage)
    return () => {
      window.removeEventListener('message', handleListenMessage)
    }
  }, [])

  useEffect(() => {
    if (isLogged) {
      const {
        id,
        last_name: lastName,
        first_name: firstName,
        name,
        email,
        phone,
        currentCompanyId,
        country_code: countryCode,
      } = accounts
      const removePlusInPhone = phone && phone.slice(1)
      const userAttribute = {
        customerId: id,
        lastName,
        firstName,
        userName: name,
        email,
        phone: removePlusInPhone,
        companyID: currentCompanyId,
        countryCode,
      }
      moengageUserAttribute(userAttribute)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogged])

  // Firing after clicking 'Reload' button of the beforeunload modal.
  // Update the browser storage before redirecting to FTL page when changing country.
  useEffect(() => {
    function handleUnload() {
      const country = window.redirectToCountry
      let language = window.redirectToLanguage
      if (language) {
        localStorage.setItem(LOCAL_STORAGE.DEFAULT_LANGUAGE, language)
      }
      if (country) {
        language = countries[country]
        language = language[country]
        localStorage.setItem(LOCAL_STORAGE.DEFAULT_COUNTRY, country)
        localStorage.setItem(LOCAL_STORAGE.DEFAULT_LANGUAGE, language)
      }
      setCookie('language', language, 1)
    }
    window.addEventListener('unload', handleUnload)
    return () => {
      window.removeEventListener('unload', handleUnload)
      window.redirectToCountry = null
    }
  }, [])

  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    const progressLoadingContainer = document.getElementById('progress-loading-container-id')
    progressLoadingContainer && progressLoadingContainer.classList.add('visible')
  }, [])

  const hideLoadingBar = () =>
    setTimeout(() => {
      // eslint-disable-next-line no-undef
      setIsLoaded(true)
      const progressLoadingContainer = document.getElementById('progress-loading-container-id')
      if (progressLoadingContainer) progressLoadingContainer.classList.remove('visible')
      hideLoading()
    }, 300)

  useEffect(() => {
    const element = document.getElementById('bar-percent')
    if (element) {
      const oldWidth = element.style.width || 0
      if (configFTL || data) {
        const percent = (parseInt(oldWidth) / 25 + 1) * 50
        element.style.width = `${percent}%`
        if (configFTL && data && percent >= 100) hideLoadingBar()
      }
    }
  }, [configFTL, data])

  useEffect(() => {
    clearLocalStorageWithHash()
    const urlParams = new URLSearchParams(window.location.search)
    const countryCodeFromUrl = urlParams.get('countrycode')
    const languageFromUrl = urlParams.get('language')
    const isLogout = urlParams.get('isLogout') === 'true'
    const companyId = urlParams.get('companyId')
    const areaId = urlParams.get('areaId') || JAKARTA_AREA
    areaId && urlParams.delete('areaId')
    isLogout && urlParams.delete('isLogout')

    history.push({ search: urlParams.toString() })

    const accessToken = localStorage.getItem(LOCAL_STORAGE.ACCESS_TOKEN)
    const decodeToken = Utils.parseJwt(accessToken)
    const company = decodeToken?.company_id || companyId !== 'undefined' ? companyId : ''

    setCountryAndGetConfig(company, countryCodeFromUrl || 'id', languageFromUrl || 'en', areaId)

    localStorage.setItem(LOCAL_STORAGE.AREA_ID, areaId || JAKARTA_AREA)

    const handleGetAuth = () => {
      const accountId = +company || +decodeToken.customer_id
      dispatch(authAction.handleGetAuth({ accountId, cb: () => setReady(true) }))
    }

    if (isLogout) {
      Utils.deleteAllStorage()
      setReady(true)
    } else if (accessToken) {
      crossStorageUtils.handleInitToken(accessToken)
      handleGetAuth()
    } else {
      const cb = (accessToken) => {
        const decodeToken = Utils.parseJwt(accessToken)
        const company = decodeToken?.company_id || companyId
        const accountId = +company || +decodeToken.customer_id
        dispatch(authAction.handleGetAuth({ accountId }))
      }
      crossStorageUtils.handleConnectCrossStorage((accessToken) => cb(accessToken))
      setReady(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts?.id])

  useEffect(() => {
    if (accounts?.id) {
      if (!accounts.sms_confirmed_at) {
        setOpenSmmVerify(true)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts?.id])

  const clearLocalStorageWithHash = () => {
    if (location.hash === CLEAR_LOCAL_STORAGE) {
      localStorage.clear()
    }
  }

  const setCountryAndGetConfig = (companyId, country, language, areaId) => {
    let countryCode = localStorage.getItem(LOCAL_STORAGE.DEFAULT_COUNTRY)
    const languageCode = language || localStorage.getItem(LOCAL_STORAGE.DEFAULT_LANGUAGE)
    if (country) {
      countryCode = country
      localStorage.setItem(LOCAL_STORAGE.DEFAULT_COUNTRY, country)
    }

    dispatch(configActions.handleGetConfiguration({ companyId, areaId }))
    dispatch(configActions.handleGetFTLConfiguration({ countryCode, languageCode }))
  }

  const isShareTrackingPage = location.pathname.startsWith('/tracking-for-non-booker')
  const isTrackingPage = location.pathname.startsWith('/tracking')

  const defaultCountry =
    Object.keys(defaultCountries).find(
      (countryValue) => countryValue === localStorage.getItem(LOCAL_STORAGE.DEFAULT_COUNTRY)
    ) || 'id'
  const country = defaultCountries[defaultCountry]

  const handleGoogleMapApi = (map) => {
    dispatch(mapAction.setMapObject(map))
  }

  if (isEmpty(configFTL) || isEmpty(data) || !ready || !isLoaded) return <Loading isOpen={true} />
  const renderNavigation = () => (
    <>
      <Navigation />
      {openSmmVerify && <SmsVerify closePopup={() => setOpenSmmVerify(false)} />}
    </>
  )
  return (
    <div className="app-wrapper">
      {!isShareTrackingPage && (
        <>
          <FreshChat />
          <Header />
        </>
      )}
      {!isTrackingPage && (
        <div className="radius4 map-wrapper">
          <GoogleMapReact
            bootstrapURLKeys={{ key: MAP.API_KEY || '' }}
            defaultCenter={{
              lat: country.center.lat,
              lng: country.center.lng,
            }}
            defaultZoom={11}
            options={(map) => ({
              mapTypeControl: true,
              mapTypeId: map.MapTypeId.ROADMAP,
              streetViewControl: false,
              mapTypeControlOptions: {
                position: map.ControlPosition.TOP_RIGHT,
                style: map.MapTypeControlStyle.HORIZONTAL_BAR,
              },
              zoomControl: true,
              zoomControlOptions: {
                position: map.ControlPosition.RIGHT_TOP,
                style: map.ZoomControlStyle.SMALL,
              },
              scaleControl: true,
              scaleControlOptions: {
                position: map.ControlPosition.RIGHT_BOTTOM,
              },
              fullscreenControl: true,
              fullscreenControlOptions: {
                position: map.ControlPosition.RIGHT_TOP,
              },
              styles: GOOGLE_MAP_STYLE,
            })}
            onGoogleApiLoaded={handleGoogleMapApi}
          >
            {!!ports?.length &&
              ports.map((port, index) => {
                if (!port) return null
                return (
                  <InfoWindow
                    key={index}
                    index={index}
                    lat={port?.location?.latitude || 0}
                    lng={port?.location?.longitude || 0}
                    port={port}
                    isP2D={serviceType === SERVICE_P2D}
                    isFrom={index === 0}
                  />
                )
              })}
            {!!expandPorts?.length &&
              expandPorts.map((port, index) => {
                if (!port) return null
                return (
                  <Port
                    key={index}
                    index={index}
                    lat={port?.location?.latitude || 0}
                    lng={port?.location?.longitude || 0}
                  />
                )
              })}
          </GoogleMapReact>
        </div>
      )}
      <Suspense fallback={<Loading isOpen={true} />}>
        {data?.enable_captcha_verification ? (
          <GoogleReCaptchaProvider reCaptchaKey={GOOGLE_RECAPTCHA_KEY} scriptProps={{ async: true, defer: true }}>
            {renderNavigation()}
          </GoogleReCaptchaProvider>
        ) : (
          renderNavigation()
        )}
      </Suspense>
      <Loading />
      <ToastContainer
        draggable
        closeOnClick
        pauseOnHover
        hideProgressBar
        position="top-center"
        autoClose={5000}
        newestOnTop={false}
        rtl={false}
        theme="colored"
      />
    </div>
  )
}

export default AppMaritime
